import type { Editor } from '@tiptap/core';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useState, useEffect } from 'react';
import { AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined, MenuOutlined } from '@ant-design/icons';

interface TextAlignDropdownMenuProps {
  editor: Editor;
}

export const TextAlignDropdownMenu = ({ editor }: TextAlignDropdownMenuProps) => {
  const [currentAlign, setCurrentAlign] = useState<'left' | 'center' | 'right' | 'justify'>('left');

  useEffect(() => {
    const updateCurrentAlign = () => {
      if (editor.isActive({ textAlign: 'center' })) {
        setCurrentAlign('center');
      } else if (editor.isActive({ textAlign: 'right' })) {
        setCurrentAlign('right');
      } else if (editor.isActive({ textAlign: 'justify' })) {
        setCurrentAlign('justify');
      } else {
        setCurrentAlign('left');
      }
    };

    // Update state immediately
    updateCurrentAlign();

    // Listen to selection changes
    editor.on('selectionUpdate', updateCurrentAlign);
    editor.on('focus', updateCurrentAlign);
    editor.on('blur', updateCurrentAlign);

    return () => {
      editor.off('selectionUpdate', updateCurrentAlign);
      editor.off('focus', updateCurrentAlign);
      editor.off('blur', updateCurrentAlign);
    };
  }, [editor]);

  const setTextAlign = (align: 'left' | 'center' | 'right' | 'justify') => {
    if (!editor.isEditable) return;
    editor.chain().focus().setTextAlign(align).run();
  };

  const alignInfo = {
    left: { text: 'Align Left', icon: <AlignLeftOutlined /> },
    center: { text: 'Align Center', icon: <AlignCenterOutlined /> },
    right: { text: 'Align Right', icon: <AlignRightOutlined /> },
    justify: { text: 'Justify', icon: <MenuOutlined /> }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'left',
      label: 'Align Left',
      icon: <AlignLeftOutlined />,
      onClick: () => setTextAlign('left')
    },
    {
      key: 'center',
      label: 'Align Center',
      icon: <AlignCenterOutlined />,
      onClick: () => setTextAlign('center')
    },
    {
      key: 'right',
      label: 'Align Right',
      icon: <AlignRightOutlined />,
      onClick: () => setTextAlign('right')
    },
    {
      key: 'justify',
      label: 'Justify',
      icon: <MenuOutlined />,
      onClick: () => setTextAlign('justify')
    }
  ];

  return (
    <Dropdown.Button
      menu={{ items: menuItems }}
      type={currentAlign !== 'left' ? 'primary' : 'default'}
      title="Text Align"
      icon={alignInfo[currentAlign].icon}
      disabled={!editor.isEditable}
    >
      {alignInfo[currentAlign].text}
    </Dropdown.Button>
  );
};
